/**
 * CollisionSystem - Handles collision detection between enemies and player army
 *
 * Features:
 * - Detects enemy-army overlaps
 * - Applies damage (soldiers lost = enemy HP)
 * - Screen shake effect on damage
 * - Hit flash effect on damage
 * - Triggers game over when army defeated
 */

import Phaser from 'phaser';
import { Player } from '../entities/Player';
import { Enemy } from '../entities/Enemy';
import { EFFECTS, ENEMIES } from '../utils/constants';
import { DURATIONS } from '../config';
import { devLog } from '../utils/devLog';

export interface CollisionSystemConfig {
  player: Player;
  getEnemies: () => Enemy[];
  getIsPaused: () => boolean;
  onGameOver: () => void;
  onEnemyCollision?: (enemy: Enemy) => void;
}

export class CollisionSystem {
  private scene: Phaser.Scene;
  private player: Player;
  private getEnemies: () => Enemy[];
  private getIsPaused: () => boolean;
  private onGameOver: () => void;
  private onEnemyCollision: (enemy: Enemy) => void;

  /** Flash overlay for hit effect */
  private flashOverlay: Phaser.GameObjects.Rectangle | null = null;

  /** Whether game over has been triggered (prevent multiple triggers) */
  private gameOverTriggered: boolean = false;

  constructor(scene: Phaser.Scene, config: CollisionSystemConfig) {
    this.scene = scene;
    this.player = config.player;
    this.getEnemies = config.getEnemies;
    this.getIsPaused = config.getIsPaused;
    this.onGameOver = config.onGameOver;
    this.onEnemyCollision = config.onEnemyCollision ?? (() => {});

    // Create flash overlay (hidden by default)
    this.createFlashOverlay();
  }

  /**
   * Creates the white flash overlay for hit effects
   */
  private createFlashOverlay(): void {
    this.flashOverlay = this.scene.add.rectangle(
      this.scene.cameras.main.centerX,
      this.scene.cameras.main.centerY,
      this.scene.cameras.main.width,
      this.scene.cameras.main.height,
      0xFFFFFF,
      0
    );
    this.flashOverlay.setScrollFactor(0);
    this.flashOverlay.setDepth(50); // Above game objects, below UI
  }

  /**
   * Update - called each frame to check for collisions
   */
  update(): void {
    if (this.getIsPaused() || this.gameOverTriggered) return;

    const enemies = this.getEnemies();
    const playerX = this.player.getArmyX();
    const playerY = this.player.getArmyY();
    const playerRadius = this.player.getFormationRadius();

    for (const enemy of enemies) {
      if (!enemy.getIsAlive()) continue;

      // Simple circle-circle collision
      const dx = enemy.x - playerX;
      const dy = enemy.y - playerY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const combinedRadius = playerRadius + enemy.getCollisionRadius();

      if (distance < combinedRadius) {
        this.handleCollision(enemy);
      }
    }
  }

  /**
   * Handles collision between enemy and player army
   */
  private handleCollision(enemy: Enemy): void {
    // Get enemy HP before destroying
    const damage = enemy.getIsFinale()
      ? this.player.getSoldierCount()
      : Math.max(1, Math.round(enemy.getHp() * ENEMIES.collisionDamageMultiplier));

    const impactX = enemy.x;
    const impactY = enemy.y;

    this.onEnemyCollision(enemy);

    // Remove the enemy (no points awarded for collision)
    enemy.removeFromPlay();

    // Flash nearby surviving soldiers before removing them
    this.flashNearbySoldiers(impactX, impactY);

    // Apply damage to player army
    this.player.removeSoldiers(damage);

    // Visual feedback
    this.playHitFlash();
    this.playScreenShake();

    devLog(`Collision! Lost ${damage} soldiers. Remaining: ${this.player.getSoldierCount()}`);

    // Check for game over
    if (this.player.isDefeated()) {
      this.triggerGameOver();
    }
  }

  /**
   * Flashes 2-3 nearby surviving soldiers red so the army visually reacts
   */
  private flashNearbySoldiers(hitX: number, hitY: number): void {
    const soldiers = this.player.getSoldiers();
    if (soldiers.length === 0) return;

    // Sort by distance to impact point, pick closest 2-3
    const sorted = [...soldiers]
      .map(s => ({
        soldier: s,
        distSq: (s.x - hitX) ** 2 + (s.y - hitY) ** 2,
      }))
      .sort((a, b) => a.distSq - b.distSq)
      .slice(0, Math.min(3, soldiers.length));

    for (const { soldier } of sorted) {
      soldier.playHitEffect();
    }
  }

  /**
   * Plays the white flash effect on taking damage
   */
  private playHitFlash(): void {
    if (!this.flashOverlay) return;

    // Flash to white and fade out
    this.flashOverlay.setAlpha(0.6);

    this.scene.tweens.add({
      targets: this.flashOverlay,
      alpha: 0,
      duration: DURATIONS.hitFlash,
      ease: 'Linear',
    });
  }

  /**
   * Plays screen shake effect on taking damage
   */
  private playScreenShake(): void {
    const { duration, intensity } = EFFECTS.screenShake;
    this.scene.cameras.main.shake(duration, intensity / 1000);
  }

  /**
   * Triggers game over sequence
   */
  private triggerGameOver(): void {
    if (this.gameOverTriggered) return;
    this.gameOverTriggered = true;

    devLog('Game Over!');

    // Brief delay before transitioning
    this.scene.time.delayedCall(500, () => {
      this.onGameOver();
    });
  }

  /**
   * Resets the system (for level restart)
   */
  reset(): void {
    this.gameOverTriggered = false;
  }

  /**
   * Cleanup
   */
  destroy(): void {
    if (this.flashOverlay) {
      this.flashOverlay.destroy();
      this.flashOverlay = null;
    }
  }
}
